import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { selectBandReport, selectBandReportByRange } from "./graphql";

export interface SelectBandItem {
  name: string;
  totalSelections: number;
  clearedCount: number;
  priorities: { priority: number; count: number }[];
}

interface AnalyticsReportRow {
  dimensionValues: string[];
  metricValues: string[];
}

interface AnalyticsReport {
  dimensionHeaders: string[];
  metricHeaders: string[];
  rows: AnalyticsReportRow[];
  totals: AnalyticsReportRow[];
  rowCount?: number | null;
}

interface SelectBandReportQuery {
  analyticsReport?: AnalyticsReport | null;
}

const normalizeValue = (value?: string | null) => (value ?? "").trim();

const parseMetricValue = (value?: string | null) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const findHeaderIndex = (headers: string[], target: string) =>
  headers.findIndex((header) => header.toLowerCase() === target.toLowerCase());

const findAnyHeaderIndex = (headers: string[], targets: string[]) =>
  targets.reduce((foundIndex, target) => {
    if (foundIndex >= 0) return foundIndex;
    return findHeaderIndex(headers, target);
  }, -1);

interface UseSelectBandReportOptions {
  startDate?: string;
  endDate?: string;
}

export const useSelectBandReport = ({ startDate, endDate }: UseSelectBandReportOptions = {}) => {
  const hasRange = !!startDate && !!endDate;
  const { data, loading, error } = useQuery<SelectBandReportQuery>(
    hasRange ? selectBandReportByRange : selectBandReport,
    hasRange ? { variables: { startDate, endDate } } : undefined
  );
  const report = data?.analyticsReport ?? null;

  const { items, labelHeader } = useMemo(() => {
    if (!report) {
      return { items: [] as SelectBandItem[], labelHeader: "Band name" };
    }

    const dimensionHeaders = report.dimensionHeaders ?? [];
    const metricHeaders = report.metricHeaders ?? [];

    const eventNameIndex = findHeaderIndex(dimensionHeaders, "eventName");
    const bandNameIndex = findAnyHeaderIndex(dimensionHeaders, [
      "bandName",
      "customEvent:bandName",
    ]);
    const priorityIndex = findAnyHeaderIndex(dimensionHeaders, [
      "priority",
      "customEvent:priority",
    ]);

    const eventCountIndex = Math.max(
      0,
      findHeaderIndex(metricHeaders, "eventCount")
    );

    // Group by bandName → priorityNumber → eventCount
    const grouped = new Map<string, Map<number, number>>();

    report.rows.forEach((row: AnalyticsReportRow) => {
      if (eventNameIndex >= 0) {
        const eventName = normalizeValue(row.dimensionValues[eventNameIndex]);
        if (eventName.toLowerCase() !== "select_band") return;
      }

      const nameIndex =
        bandNameIndex >= 0 ? bandNameIndex : Math.max(0, eventNameIndex);
      const name = normalizeValue(row.dimensionValues[nameIndex]) || "Unknown";
      const priorityStr = normalizeValue(row.dimensionValues[priorityIndex]);
      const priority = parseInt(priorityStr, 10);
      const eventCount = parseMetricValue(row.metricValues[eventCountIndex]);

      if (!grouped.has(name)) grouped.set(name, new Map());
      const priorityMap = grouped.get(name)!;
      priorityMap.set(priority, (priorityMap.get(priority) ?? 0) + eventCount);
    });

    // Compute net selections: net[N] = count[N] - count[N+1]
    // Press 5 = clear, so cleared = count[5]
    const allItems: SelectBandItem[] = Array.from(grouped.entries()).map(
      ([name, priorityMap]) => {
        const count = (p: number) => priorityMap.get(p) ?? 0;

        const priorities = [1, 2, 3, 4]
          .map((p) => ({ priority: p, count: Math.max(0, count(p) - count(p + 1)) }))
          .filter((p) => p.count > 0);

        const clearedCount = count(5);
        const totalSelections = Math.max(0, count(1) - clearedCount);

        return { name, totalSelections, clearedCount, priorities };
      }
    );

    const sortedItems = allItems
      .filter((item) => item.totalSelections > 0)
      .sort((a, b) => b.totalSelections - a.totalSelections);
    const header = bandNameIndex >= 0 ? "Band name" : "Event name";

    return { items: sortedItems, labelHeader: header };
  }, [report]);

  return { items, labelHeader, loading, error };
};
